import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { CompaniesService } from '@/services/companies'
import type { Company } from '@/types/company'

interface CompaniesTableProps {
	onCompanyClick?: (company: Company) => void
}

export function CompaniesTable({ onCompanyClick }: CompaniesTableProps) {
	const [companies, setCompanies] = useState<Company[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)
	const [isSheetOpen, setIsSheetOpen] = useState(false)
	const [isCreating, setIsCreating] = useState(false)
	const [formCode, setFormCode] = useState('')
	const [formName, setFormName] = useState('')
	const [formActive, setFormActive] = useState(false)
	const [saving, setSaving] = useState(false)

	const fetchCompanies = async () => {
		try {
			setLoading(true)
			setError(null)
			const companiesData = await CompaniesService.getCompanies()
			setCompanies(companiesData || [])
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Error al cargar empresas')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchCompanies()
	}, [])

	const handleCreateClick = () => {
		setSelectedCompany(null)
		setIsCreating(true)
		setFormCode('')
		setFormName('')
		setFormActive(true)
		setError(null)
		setIsSheetOpen(true)
	}

	const handleRowClick = (company: Company) => {
		if (onCompanyClick) {
			onCompanyClick(company)
		}
	}

	const handleSave = async () => {
		try {
			setSaving(true)
			setError(null)

			if (isCreating) {
				// Crear nueva empresa
				await CompaniesService.createCompany({
					code: formCode,
					name: formName,
					active: formActive,
				})
				toast.success('Empresa creada correctamente')
			} else if (selectedCompany) {
				// Actualizar empresa existente
				await CompaniesService.updateCompany({
					id: selectedCompany.id,
					code: selectedCompany.code,
					name: formName,
					active: formActive,
				})
				toast.success('Empresa actualizada correctamente')
			}

			setIsSheetOpen(false)
			// Refrescar los datos
			await fetchCompanies()
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: isCreating
						? 'Error al crear empresa'
						: 'Error al actualizar empresa'
			setError(errorMessage)
		} finally {
			setSaving(false)
		}
	}

	if (error && !loading) {
		return (
			<div className="container mx-auto p-8">
				<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
					{error}
				</div>
			</div>
		)
	}

	return (
		<div className="container mx-auto p-2">
			<div className="max-w-6xl mx-auto space-y-6 pt-10">
				<div>
					<h1 className="text-3xl font-bold">Empresas</h1>
					<p className="text-muted-foreground">Gestiona las empresas del sistema</p>
				</div>

				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<div>
								<CardTitle>Lista de empresas</CardTitle>
								<CardDescription>
									{loading ? (
										<Skeleton className="h-4 w-32" />
									) : (
										<>
											{companies.length} {companies.length === 1 ? 'empresa' : 'empresas'} en
											total
										</>
									)}
								</CardDescription>
							</div>
							<Button onClick={handleCreateClick} disabled={loading}>
								Crear empresa
							</Button>
						</div>
					</CardHeader>
					<CardContent>
						{loading ? (
							<div className="space-y-3">
								<Skeleton className="h-12 w-full" />
								<Skeleton className="h-12 w-full" />
								<Skeleton className="h-12 w-full" />
							</div>
						) : (
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Código</TableHead>
										<TableHead>Nombre</TableHead>
										<TableHead>Estado</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{companies.length === 0 ? (
										<TableRow>
											<TableCell colSpan={3} className="text-center text-muted-foreground">
												No hay empresas disponibles
											</TableCell>
										</TableRow>
									) : (
										companies.map((company) => (
											<TableRow
												key={company.id}
												onClick={() => handleRowClick(company)}
												className="cursor-pointer"
											>
												<TableCell>{company.code}</TableCell>
												<TableCell className="font-medium">{company.name}</TableCell>
												<TableCell>
													<Badge
														variant={company.active ? 'default' : 'outline'}
														className={
															company.active
																? 'bg-green-500/10 text-green-500 border-green-500/20 hover:bg-green-500/20'
																: 'bg-gray-500/10 text-gray-500 border-gray-500/20 hover:bg-gray-500/20'
														}
													>
														{company.active ? 'Activa' : 'Inactiva'}
													</Badge>
												</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						)}
					</CardContent>
				</Card>

				<Sheet
					open={isSheetOpen}
					onOpenChange={(open) => {
						setIsSheetOpen(open)
						if (!open) {
							setError(null)
							setIsCreating(false)
							setSelectedCompany(null)
						}
					}}
				>
					<SheetContent>
						<SheetHeader className="space-y-2 pb-4">
							<SheetTitle>{isCreating ? 'Crear empresa' : 'Editar empresa'}</SheetTitle>
							<SheetDescription>
								{isCreating
									? 'Completa los datos para crear una nueva empresa.'
									: 'Modifica los datos de la empresa. Los cambios se guardarán al hacer clic en Guardar.'}
							</SheetDescription>
						</SheetHeader>
						<div className="flex-1 py-4 space-y-4 px-4">
							<div className="space-y-2">
								<Label htmlFor="company-code">Código</Label>
								<Input
									id="company-code"
									value={formCode}
									onChange={(e) => setFormCode(e.target.value)}
									disabled={!isCreating}
									className={!isCreating ? 'bg-muted' : ''}
									placeholder="Código de la empresa"
									required={isCreating}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="company-name">Nombre</Label>
								<Input
									id="company-name"
									value={formName}
									onChange={(e) => setFormName(e.target.value)}
									placeholder="Nombre de la empresa"
									required
								/>
							</div>
							<div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
								<input
									type="checkbox"
									id="company-active"
									checked={formActive}
									onChange={(e) => setFormActive(e.target.checked)}
									className="h-4 w-4 rounded border-gray-300 cursor-pointer"
								/>
								<Label htmlFor="company-active" className="cursor-pointer flex-1">
									Empresa activa
								</Label>
							</div>
							{error && (
								<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
									{error}
								</div>
							)}
						</div>
						<SheetFooter className="pt-4">
							<Button
								onClick={handleSave}
								disabled={saving || !formName.trim() || (isCreating && !formCode.trim())}
							>
								{saving ? 'Guardando...' : isCreating ? 'Crear empresa' : 'Guardar cambios'}
							</Button>
						</SheetFooter>
					</SheetContent>
				</Sheet>
			</div>
		</div>
	)
}
